/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from 'react';

import * as styles from './styles';

import { getVehicleLabel, type UUID, type Vehicle, type VehicleGroup } from '@cobliteam/common-types';

import { isWebDataError, isWebDataSuccess, type PastMaintenance, type PastMaintenancesMaps, type PastMaintenanceWithVehicle, type WebData } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import { useVehicleGroup } from '@cobliteam/hooks';

import { Chip, DefaultInput, Heading1, InputLabel, NoConnectionIcon, Placeholder, PlaceholderRelaxIcon, VehicleGroupFilter } from '@cobliteam/base-components';

import { MultiSelectVehicles } from '@cobliteam/ui/business';
import { Button } from '@cobliteam/ui/components';

import { AddRounded, SearchRounded } from '@mui/icons-material';

import { AccordionPlaceholder } from './components/AccordionPlaceholder/AccordionPlaceholder';
import { VehicleMaintenanceTable } from './components/VehicleMaintenanceTable/VehicleMaintenanceTable';

export interface MaintenanceHistoryProps {
  pastMaintenancesMapsWebData: WebData<PastMaintenancesMaps>;
  vehiclesWebData: WebData<Vehicle[]>;
  onRegisterMaintenanceClick: () => void;
  onEditMaintenanceClick: (maintenance: PastMaintenance) => void;
  onLoadPageContent: () => void;
}

export const MaintenanceHistory: React.FC<MaintenanceHistoryProps> = ({
  pastMaintenancesMapsWebData,
  vehiclesWebData,
  onRegisterMaintenanceClick,
  onEditMaintenanceClick,
  onLoadPageContent,
}) => {
  const [fieldQuery, setFieldQuery] = useState<string>('');
  const [wantedVehicleIds, setWantedVehicleIds] = useState<UUID[] | undefined>(undefined);
  const [wantedVehicleGroupIds, setWantedVehicleGroupIds] = useState<UUID[] | undefined>(undefined);

  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups, isSuccess: isVehicleGroupsSuccess, refetch: fetchVehicleGroups } = getVehicleGroups();

  const vehiclesMap: Map<string, Vehicle> = useMemo(
    () => (isWebDataSuccess(vehiclesWebData) && new Map(vehiclesWebData.data.map((vehicle) => [vehicle.id, vehicle]))) || new Map(),
    [vehiclesWebData]
  );
  const vehicleGroupsMap: Map<string, VehicleGroup> = useMemo(
    () => (isVehicleGroupsSuccess && new Map(vehicleGroups.map((vehicleGroup) => [vehicleGroup.id, vehicleGroup]))) || new Map(),
    [isVehicleGroupsSuccess, vehicleGroups]
  );

  useEffect(() => {
    fetchVehicleGroups();
  }, [fetchVehicleGroups]);

  React.useEffect(() => {
    Analytics.track('Maintenance History Viewed');
    onLoadPageContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReloadOnError = () => {
    Analytics.track('Maintenance History Reloaded On Error');
    onLoadPageContent();
  };

  const handleMaintenanceEdit = (maintenance: PastMaintenance) => {
    Analytics.track('Maintenance History Edit Maintenance Button Clicked');
    onEditMaintenanceClick(maintenance);
  };

  const handleRegisterMaintenance = () => {
    Analytics.track('Maintenance History Register Maintenance Button Clicked');
    onRegisterMaintenanceClick();
  };

  const updateFieldSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    Analytics.track('Maintenance History Vehicle Search Updated');

    setFieldQuery(value);
  };

  const getVehiclesWithMaintenances = (vehicles: Vehicle[], pastMaintenances: PastMaintenancesMaps): PastMaintenanceWithVehicle[] => {
    const rawPastMaintenances = Object.values(pastMaintenances.pastMaintenancesById);

    const vehiclesById = vehicles.reduce(
      (acc, vehicle) => {
        acc[vehicle.id] = vehicle;
        return acc;
      },
      {} as Record<string, Vehicle>
    );

    return rawPastMaintenances
      .filter((pastMaintenance) => vehiclesById[pastMaintenance.vehicleId])
      .map((pastMaintenance) => ({
        vehicle: vehiclesById[pastMaintenance.vehicleId],
        ...pastMaintenance,
      }));
  };

  const matchesSearchQuery = (vehicle: string) => {
    const treatedFieldQuery = fieldQuery
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const treatedData = vehicle
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    return treatedData.includes(treatedFieldQuery);
  };

  const handleVehicleFilterApply = (selectedVehicles: Vehicle[]) => {
    const vehicleIds = selectedVehicles.map((vehicle) => vehicle.id);
    setWantedVehicleIds(vehicleIds);
  };

  const handleClearVehicleFilter = () => {
    Analytics.track('Vehicle Filter On Maintenance History Cleared');
  };

  const handleVehicleChipDelete = (deletedVehicleid: UUID) => {
    const newWantedvehicleids = wantedVehicleIds?.filter((vehicleId) => vehicleId !== deletedVehicleid);
    setWantedVehicleIds(newWantedvehicleids);
  };

  const handleVehicleGroupFilterApply = (selectedVehicleGroups: VehicleGroup[]) => {
    const vehicleGroupIds = selectedVehicleGroups.map((vehicleGroup) => vehicleGroup.id);
    setWantedVehicleGroupIds(vehicleGroupIds);
  };

  const handleClearVehicleGroupFilter = () => {
    Analytics.track('Vehicle Group Filter On Maintenance History Cleared');
  };

  const handleVehicleGroupChipDelete = (deletedVehicleGroupid: UUID) => {
    const newWantedvehicleGroupids = wantedVehicleGroupIds?.filter((vehicleGroupId) => vehicleGroupId !== deletedVehicleGroupid);
    setWantedVehicleGroupIds(newWantedvehicleGroupids);
  };

  const isIdListUndefinedOrEmpty = (idsList?: UUID[]): boolean => idsList === undefined || idsList.length === 0;

  const renderContent = () => {
    if (isWebDataError(pastMaintenancesMapsWebData) || isWebDataError(vehiclesWebData)) {
      return (
        <Placeholder className={styles.placeholderWithTextButton} text='Ops! Ocorreu um erro no sistema.' Icon={NoConnectionIcon}>
          <Button variant='text' onClick={handleReloadOnError}>
            Recarregar
          </Button>
        </Placeholder>
      );
    }
    if (isWebDataSuccess(pastMaintenancesMapsWebData) && isWebDataSuccess(vehiclesWebData)) {
      const maintenancesWithVehicles = getVehiclesWithMaintenances(vehiclesWebData.data, pastMaintenancesMapsWebData.data);

      const filteredMaintenances = maintenancesWithVehicles.filter((maintenanceWithVehicle) => {
        const { vehicle } = maintenanceWithVehicle;
        const vehicleGroupId = (vehicle.groups && vehicle.groups.length > 0 && vehicle.groups[0].id) || undefined;
        const vehicleLabel = getVehicleLabel(vehicle.licensePlate, vehicle.model.name, vehicle.model.brand, vehicle.cobliId);

        const vehicleFilterIsNullOrEmpty = isIdListUndefinedOrEmpty(wantedVehicleIds);
        const vehicleGroupFilterIsNullOrEmpty = isIdListUndefinedOrEmpty(wantedVehicleGroupIds);

        return (
          matchesSearchQuery(vehicleLabel) &&
          ((!vehicleFilterIsNullOrEmpty && wantedVehicleIds!.includes(vehicle.id)) ||
            (vehicleGroupId && !vehicleGroupFilterIsNullOrEmpty && wantedVehicleGroupIds!.includes(vehicleGroupId)) ||
            (vehicleFilterIsNullOrEmpty && vehicleGroupFilterIsNullOrEmpty))
        );
      });

      if (maintenancesWithVehicles.length === 0) {
        return (
          <Placeholder className={styles.placeholderWithTextButton} text='Você ainda não tem registro de manutenções no seu histórico.' Icon={PlaceholderRelaxIcon}>
            <Button variant='text' onClick={handleRegisterMaintenance}>
              Registrar
            </Button>
          </Placeholder>
        );
      }

      if (filteredMaintenances.length === 0) {
        return <Placeholder className={styles.placeholderWithoutTextButton} text='Nenhum resultado encontrado. Ajuste os filtros aplicados.' Icon={PlaceholderRelaxIcon} />;
      }

      return <VehicleMaintenanceTable pastMaintenances={filteredMaintenances} onEditClick={handleMaintenanceEdit} />;
    }
    return <AccordionPlaceholder />;
  };

  const renderedFilterSection = useMemo(() => {
    if (isWebDataSuccess(vehiclesWebData) && isVehicleGroupsSuccess) {
      return (
        <div className={styles.searchAndRegisterWrapper}>
          <div className={styles.filters}>
            <div className={styles.searchBoxFilter}>
              <DefaultInput name='search' className={styles.searchField} placeholder='Procure por veículo' startAdornment={<SearchRounded />} onChange={updateFieldSearch} />
            </div>
            <VehicleGroupFilter
              vehicleGroups={vehicleGroups}
              appliedVehicleGroups={new Set(wantedVehicleGroupIds || [])}
              applyVehicleGroupsFilter={handleVehicleGroupFilterApply}
              onCancel={handleClearVehicleGroupFilter}
            />
            <MultiSelectVehicles
              selectedVehicles={vehiclesWebData.data.filter((v) => wantedVehicleIds?.includes(v.id))}
              onChange={handleVehicleFilterApply}
              onClear={handleClearVehicleFilter}
            />
          </div>
          <Button size='condensed' startIcon={<AddRounded />} onClick={handleRegisterMaintenance}>
            Registrar manutenção
          </Button>
        </div>
      );
    }
    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclesWebData, vehicleGroups, wantedVehicleIds, wantedVehicleGroupIds]);

  const renderedFilterChips = useMemo(() => {
    const hasLoadedVehiclesAndGroups = isWebDataSuccess(vehiclesWebData) && isVehicleGroupsSuccess;
    const hasFiltersApplied = !isIdListUndefinedOrEmpty(wantedVehicleIds) || !isIdListUndefinedOrEmpty(wantedVehicleGroupIds);
    if (hasLoadedVehiclesAndGroups && hasFiltersApplied) {
      const selectedVehicles = wantedVehicleIds?.map((vehicleId) => vehiclesMap.get(vehicleId)).filter((vehicle) => vehicle !== undefined) || [];

      const selectedVehicleGroups = wantedVehicleGroupIds?.map((vehicleGroupId) => vehicleGroupsMap.get(vehicleGroupId)).filter((vehicleGroup) => vehicleGroup !== undefined) || [];

      return (
        <div className={styles.chipsWrapper}>
          <InputLabel>Visualizando:</InputLabel>
          {selectedVehicleGroups.map((vehicleGroup, index) => (
            <Chip key={index} label={vehicleGroup!.name} onDelete={() => handleVehicleGroupChipDelete(vehicleGroup!.id)} />
          ))}
          {selectedVehicles.map((vehicle, index) => (
            <Chip
              key={selectedVehicleGroups.length + index}
              label={getVehicleLabel(vehicle!.licensePlate, vehicle!.model.name, vehicle!.model.brand, vehicle!.cobliId)}
              onDelete={() => handleVehicleChipDelete(vehicle!.id)}
            />
          ))}
        </div>
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedVehicleIds, wantedVehicleGroupIds, vehiclesMap, vehicleGroupsMap]);

  return (
    <>
      <div className={styles.header}>
        <Heading1>Histórico de manutenções</Heading1>
      </div>

      {renderedFilterSection}
      {renderedFilterChips}
      {renderContent()}
    </>
  );
};
